import { useState, useEffect, useRef } from "react";

import useEmblaCarousel from "embla-carousel-react";
import Lottie from "lottie-react";
import { Play, Info, X, Share2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import downArrowAnimation from "../assets/down_arrow.json";
import LeftMenu from "../components/LeftMenu";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import PremiumOverlay from "../components/PremiumOverlay";
import SeriesCarousel from "../components/SeriesCarousel";
import SmartAppBanner from "../components/SmartAppBanner";
import apiService from "../services/api";
import { debugLog } from "../utils/debugLog";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import { isPremiumUser } from "../utils/premiumUtils";
import watchProgressUtils from "../utils/watchProgressUtils";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerSeriesDetails = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const fetchEpisodeProgressRef = useRef(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [trailerPlaying, setTrailerPlaying] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);
  const [showProviders, setShowProviders] = useState(false);
  const [streamingProviders, setStreamingProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [activeTab, setActiveTab] = useState("episodes"); // "episodes" or "suggested"
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [episodeProgress, setEpisodeProgress] = useState({});
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [showDescriptionOverlay, setShowDescriptionOverlay] = useState(false);

  // Embla carousel for cast
  const [castEmblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 3,
    containScroll: "keepSnaps",
    dragFree: true,
  });

  // Get profile and playlist from storage
  const profile = JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist = JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  useEffect(() => {
    async function fetchSeriesDetails(id) {
      try {
        setLoading(true);
        let seriesData = null;
        let tmdbData = null;
        const token = webPlayerStorage.getToken();

        // If user is logged in, try authenticated fetch first
        if (token) {
          try {
            const response = await apiService.webPlayerGetSeriesDetails(id);

            seriesData = response?.data || response;
            // Check if we got valid data (not "Not Available")
            if (
              seriesData?.name === "Not Available" ||
              seriesData?.mediaId === -1
            ) {
              seriesData = null;
            }
          } catch {
            // Auth fetch failed, will try other methods below
            seriesData = null;
          }
        }

        // If no valid data yet, try NoAuth
        if (!seriesData) {
          try {
            const noAuthResponse = await apiService.webPlayerRequest(
              `/v2/series/${id}`,
              {
                method: "GET",
                headers: {
                  Authorization: "Bearer NoAuth",
                },
              }
            );
            const noAuthData = noAuthResponse?.data || noAuthResponse;

            // Check if NoAuth returned valid data
            if (
              noAuthData?.name &&
              noAuthData.name !== "Not Available" &&
              noAuthData?.mediaId !== -1
            ) {
              // If logged in, try to find series in user's library by searching
              if (token && noAuthData?.tmdbId && noAuthData?.name) {
                try {
                  const searchResponse = await apiService.webPlayerRequest(
                    `/v2/series?term=${encodeURIComponent(noAuthData.name)}&limit=20`,
                    { method: "GET" }
                  );
                  const searchResults = searchResponse?.data || [];
                  const userSeries = searchResults.find(
                    (s) => s.tmdbId === noAuthData.tmdbId
                  );

                  if (userSeries?.mediaId) {
                    const fullResponse =
                      await apiService.webPlayerGetSeriesDetails(
                        userSeries.mediaId
                      );

                    seriesData = fullResponse?.data || fullResponse;
                  }
                } catch {
                  // Search failed, use NoAuth data
                }
              }

              if (!seriesData) {
                seriesData = noAuthData;
              }
            }
          } catch {
            // NoAuth also failed
          }
        }

        // If still no data, try TMDB directly (ID might be a tmdbId from shared link)
        // Use Vercel endpoint since /api/tmdb doesn't exist on main server
        if (!seriesData) {
          try {
            const tmdbResponse = await fetch(
              `https://web-player-lunovas-projects-076fd788.vercel.app/api/tmdb?id=${id}&type=tv`
            );

            if (tmdbResponse.ok) {
              tmdbData = await tmdbResponse.json();

              // If user is logged in, search their library by series name
              if (token && tmdbData?.name) {
                try {
                  const searchResponse = await apiService.webPlayerRequest(
                    `/v2/series?term=${encodeURIComponent(tmdbData.name)}&limit=20`,
                    { method: "GET" }
                  );
                  const searchResults = searchResponse?.data || [];

                  // Find series with matching tmdbId
                  const userSeries = searchResults.find(
                    (s) =>
                      s.tmdbId === tmdbData.tmdbId ||
                      s.tmdbId === parseInt(id, 10)
                  );

                  if (userSeries?.mediaId) {
                    const fullResponse =
                      await apiService.webPlayerGetSeriesDetails(
                        userSeries.mediaId
                      );
                    const userData = fullResponse?.data || fullResponse;

                    // Only use user data if it's valid (not "Not Available")
                    if (
                      userData?.name &&
                      userData.name !== "Not Available" &&
                      userData?.mediaId !== -1
                    ) {
                      seriesData = userData;
                    }
                  }
                } catch {
                  // Search failed, use TMDB data as fallback
                }
              }

              // Use TMDB data if couldn't find valid data in user's library
              if (!seriesData) {
                seriesData = tmdbData;
              }
            }
          } catch {
            // TMDB fetch failed
          }
        }

        if (seriesData) {
          setSeries(seriesData);
          if (seriesData.isBookmarked) {
            setIsBookmarked(true);
          }
          // Set default tab based on available content
          if (
            (!seriesData.episodes || seriesData.episodes.length === 0) &&
            seriesData.recommendations &&
            seriesData.recommendations.length > 0
          ) {
            setActiveTab("suggested");
          }
        }
      } catch (error) {
        toast.error("Failed to load series details");
      } finally {
        setLoading(false);
      }
    }

    fetchSeriesDetails(seriesId);
  }, [seriesId]);

  // Fetch episode progress when series loads or changes
  useEffect(() => {
    if (series?.seasons) {
      fetchEpisodeProgressRef.current?.();
    }
  }, [series?.seasons, selectedSeason]);

  const fetchEpisodeProgress = () => {
    if (!series?.seasons || !Array.isArray(series.seasons)) return;

    const progressMap = {};

    // Get all episodes from all seasons
    series.seasons.forEach((season) => {
      if (!season.episodes || !Array.isArray(season.episodes)) return;

      season.episodes.forEach((episode) => {
        const episodeMediaId = `${series.mediaId || series.id}-${episode.episodeId || episode.id}`;
        const progressData = watchProgressUtils.getValidatedProgress(
          episodeMediaId,
          "tv"
        );

        if (progressData.isValid && progressData.progressPercent > 0) {
          progressMap[episodeMediaId] = progressData.progressPercent;
        }
      });
    });

    setEpisodeProgress(progressMap);
  };

  fetchEpisodeProgressRef.current = fetchEpisodeProgress;

  const toggleBookmark = async () => {
    try {
      if (!isBookmarked) {
        await apiService.webPlayerCreateBookmark(
          series.mediaId,
          series.mediaType
        );
      } else {
        await apiService.webPlayerDeleteBookmark(
          series.mediaId,
          series.mediaType
        );
      }

      setIsBookmarked(!isBookmarked);
      toast.success(
        isBookmarked ? "Removed from favorites" : "Added to favorites"
      );
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const fetchStreamingProviders = async () => {
    setLoadingProviders(true);
    try {
      if (series.streamingProviders) {
        setStreamingProviders(series.streamingProviders);
      }
    } catch (error) {
      console.error("Error fetching streaming providers:", error);
      setStreamingProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleWhereToWatch = () => {
    setShowProviders(true);
    fetchStreamingProviders();
  };

  // Check if user is authenticated
  const isAuthenticated = !!webPlayerStorage.getToken();

  const handlePlayEpisode = (episode) => {
    // If not authenticated, show "Where to Watch" overlay
    if (!isAuthenticated) {
      handleWhereToWatch();

      return;
    }

    // Check premium status - only show overlay if logged in but not premium
    if (!isPremiumUser()) {
      setShowPremiumOverlay(true);

      return;
    }

    // Construct the stream URL for the episode
    let streamUrl = null;

    // Check if episode has a direct stream URL
    if (episode?.streamUrl) {
      try {
        // Try to decode if it's base64
        if (!episode.streamUrl.startsWith("http")) {
          streamUrl = atob(episode.streamUrl);
        } else {
          ({ streamUrl } = episode);
        }
      } catch (error) {
        console.error("Error decoding stream URL:", error);
        ({ streamUrl } = episode); // Use as-is if decode fails
      }
    } else if (series?.id && episode && playlist) {
      // Construct URL using playlist data for series episodes
      if (playlist.url && playlist.username && playlist.password) {
        const baseUrl = playlist.url.replace(/\/$/, "");
        // For series episodes, construct appropriate URL
        const episodeId =
          episode.id ||
          `${series.id}_s${episode.season || 1}_e${episode.episode}`;

        streamUrl = `${baseUrl}/series/${playlist.username}/${playlist.password}/${episodeId}`;
      }
    }

    if (streamUrl) {
      debugLog("Playing episode with URL:", streamUrl);
      setSelectedEpisode(episode);
      setCurrentStreamUrl(streamUrl);
      setCurrentStreamTitle(`${series.name} - ${formatEpisodeTitle(episode)}`);
      setPlayerOpen(true);
    } else {
      toast.error("Episode stream not available");
    }
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/
    );

    return match ? match[1] : null;
  };

  const formatDuration = (duration) => {
    if (!duration && duration !== 0) return "";

    let minutes = duration;

    // Handle string durations
    if (typeof duration === "string") {
      // Handle HH:MM:SS format (e.g., "00:45:00" or "01:30:00")
      const timeMatch = duration.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);

      if (timeMatch) {
        const hours = parseInt(timeMatch[1], 10);
        const mins = parseInt(timeMatch[2], 10);
        const secs = parseInt(timeMatch[3], 10);

        minutes = hours * 60 + mins + (secs > 30 ? 1 : 0);
      } else {
        // Try to parse "Xh Ym" or "X min" format
        const hourMatch = duration.match(/(\d+)\s*h/i);
        const minMatch = duration.match(/(\d+)\s*m/i);

        if (hourMatch || minMatch) {
          const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
          const mins = minMatch ? parseInt(minMatch[1], 10) : 0;

          minutes = hours * 60 + mins;
        } else {
          // Try parsing as a number
          minutes = parseFloat(duration);
        }
      }
    }

    // If duration is in seconds (typically > 1000), convert to minutes
    if (minutes > 1000) {
      minutes = Math.round(minutes / 60);
    }

    // Validate we have a valid number
    if (isNaN(minutes) || minutes <= 0) return "";

    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }

    return `${mins}m`;
  };

  const formatEpisodeTitle = (episode) => {
    const cleanName =
      episode.name?.replace(/^.*?S\d+E\d+\s*-?\s*/, "") ||
      `Episode ${episode.episode}`;

    return `S${String(episode.season).padStart(2, "0")}E${String(
      episode.episode
    ).padStart(2, "0")} - ${cleanName}`;
  };

  const handleShare = async () => {
    // Build share URL using tmdbId for OG tags (crawlers fetch from TMDB)
    const shareUrl = series?.tmdbId
      ? `${window.location.origin}/web-player/series/${series.tmdbId}`
      : `${window.location.origin}/web-player/series/${seriesId}`;
    const shareTitle = series?.name || "Check out this series";
    const shareText = series?.description
      ? `${shareTitle} - ${series.description.slice(0, 100)}...`
      : shareTitle;

    // Try native share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, fall back to clipboard
        if (err.name !== "AbortError") {
          copyToClipboard(shareUrl);
        }
      }
    } else {
      // Fall back to clipboard copy
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");

      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Link copied to clipboard!");
    }
  };

  // Filter episodes by selected season
  const seasonEpisodes = series?.episodes?.filter(
    (episode) => episode.season === selectedSeason
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-white text-xl">Series not found</p>
      </div>
    );
  }

  return (
    <>
      {/* Dynamic Meta Tags for Sharing */}
      <Helmet>
        <title>
          {series?.name
            ? `${series.name} (${series.year || ""}) - Lunova`
            : "Series - Lunova"}
        </title>
        <meta
          content={series?.description || "Watch this series on Lunova"}
          name="description"
        />

        {/* Open Graph */}
        <meta content="video.tv_show" property="og:type" />
        <meta
          content={
            series?.name ? `${series.name} (${series.year || ""})` : "Series"
          }
          property="og:title"
        />
        <meta
          content={series?.description || "Watch this series on Lunova"}
          property="og:description"
        />
        <meta
          content={
            series?.backdropPath || series?.posterPath || "/webplayer.jpg"
          }
          property="og:image"
        />
        <meta
          content={`https://play.lunova.app/web-player/series/${seriesId}`}
          property="og:url"
        />
        <meta content="Lunova" property="og:site_name" />

        {/* Twitter Card */}
        <meta content="summary_large_image" name="twitter:card" />
        <meta
          content={
            series?.name ? `${series.name} (${series.year || ""})` : "Series"
          }
          name="twitter:title"
        />
        <meta
          content={series?.description || "Watch this series on Lunova"}
          name="twitter:description"
        />
        <meta
          content={
            series?.backdropPath || series?.posterPath || "/webplayer.jpg"
          }
          name="twitter:image"
        />

        {/* App Links */}
        <meta
          content="app-id=6504567890, app-argument=lunova://series/{seriesId}"
          name="apple-itunes-app"
        />
      </Helmet>

      <SmartAppBanner />

      {/* Mobile Menu */}
      <MobileMenu />

      <div className="min-h-screen text-white relative">
        {/* Fixed gradient background for entire page */}
        <div className="fixed inset-0 bg-gradient-to-b from-gray-600 via-gray-800 to-black z-0" />

        {/* Dashboard Button - Bottom Right */}
        <button
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
          onClick={() => {
            // Clear all session data
            webPlayerStorage.clearAll();
            // Redirect to dashboard
            window.location.href = "https://dashboard.lunova.app";
          }}
        >
          Dashboard
        </button>

        {/* Left Menu */}
        <LeftMenu
          activeMenuItem="series"
          playlist={playlist}
          profile={profile}
        />

        {/* Backdrop Hero Section - Full viewport height */}
        <div className="relative h-screen z-10">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${series.backdropPath})`,
            }}
          >
            {/* Gradient Overlays - Lighter for better image visibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
          </div>

          {/* Series Info - Positioned at bottom of viewport */}
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16 z-20">
            <div className="max-w-7xl mx-auto">
              {/* Title and Logo */}
              <div className="mb-6">
                {series.logoPath ? (
                  <img
                    alt={series.name}
                    className="h-20 lg:h-32 object-contain mb-4"
                    src={series.logoPath}
                  />
                ) : (
                  <h1 className="text-4xl lg:text-6xl font-bold mb-4">
                    {series.name}
                  </h1>
                )}
              </div>

              {/* Metadata Row - Same layout as Movies */}
              <div className="flex gap-4 mb-3">
                {/* Rating Badge */}
                <div className="inline-flex items-center justify-center bg-black/60 backdrop-blur-sm border-2 border-gray-500 px-3 py-2 rounded text-white text-base font-bold min-h-[40px]">
                  {series.certification || series.rating || "NR"}
                </div>

                {/* Content next to rating */}
                <div className="flex items-center gap-3 text-white/90 text-base">
                  {series.genres && Array.isArray(series.genres) && (
                    <>
                      <span>
                        {series.genres
                          .slice(0, 3)
                          .map((g) => (typeof g === "string" ? g : g.name))
                          .join(", ")}
                      </span>
                      <span className="text-white/50">•</span>
                    </>
                  )}
                  {series.releaseDate && (
                    <>
                      <span>{new Date(series.releaseDate).getFullYear()}</span>
                      <span className="text-white/50">•</span>
                    </>
                  )}
                  <span>Series</span>
                  {series.seasons && Array.isArray(series.seasons) && (
                    <>
                      <span className="text-white/50">•</span>
                      <span>
                        {series.seasons.filter((s) => s.season !== 0).length}{" "}
                        Season
                        {series.seasons.filter((s) => s.season !== 0).length > 1
                          ? "s"
                          : ""}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Ratings */}
              <div className="flex items-center gap-6 mb-6">
                {series.rottenTomatoes?.critics?.score && (
                  <div className="flex items-center gap-2">
                    <img
                      alt="Rotten Tomatoes"
                      className="w-6 h-6"
                      src={
                        series.rottenTomatoes.critics.icon ||
                        "/rotten-tomatoes.png"
                      }
                    />
                    <span className="font-bold text-lg">
                      {series.rottenTomatoes.critics.score}%
                    </span>
                  </div>
                )}
                {series.rottenTomatoes?.audience?.score && (
                  <div className="flex items-center gap-2">
                    <img
                      alt="Audience Score"
                      className="w-6 h-6"
                      src={
                        series.rottenTomatoes.audience.icon || "/popcorn.png"
                      }
                    />
                    <span className="font-bold text-lg">
                      {series.rottenTomatoes.audience.score}%
                    </span>
                  </div>
                )}
                {series.imdbRating && (
                  <div className="flex items-center gap-2">
                    <img alt="IMDB" className="w-6 h-6" src="/imdb.png" />
                    <span className="font-bold text-lg">
                      {series.imdbRating}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              {(series.description || series.overview) && (
                <p
                  aria-label="Read full description"
                  className="text-gray-300 max-w-3xl mb-8 line-clamp-3 cursor-pointer hover:text-white transition-colors"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // Check if text is truncated (has ellipsis)
                    if (
                      e.currentTarget.scrollHeight >
                      e.currentTarget.clientHeight
                    ) {
                      setShowDescriptionOverlay(true);
                    }
                  }}
                  onKeyDown={activateOnEnterOrSpace}
                >
                  {series.description || series.overview}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 flex-wrap">
                {isAuthenticated ? (
                  <button
                    className="flex items-center gap-2 px-8 py-3 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-colors"
                    onClick={() => {
                      if (seasonEpisodes && seasonEpisodes.length > 0) {
                        handlePlayEpisode(seasonEpisodes[0]);
                      } else if (
                        series.episodes &&
                        series.episodes.length > 0
                      ) {
                        handlePlayEpisode(series.episodes[0]);
                      }
                    }}
                  >
                    <Play className="w-5 h-5 fill-current" />
                    <span>Play S{selectedSeason}E1</span>
                  </button>
                ) : (
                  <button
                    className="flex items-center gap-2 px-8 py-3 bg-white text-black rounded-full font-semibold hover:bg-gray-200 transition-colors"
                    onClick={handleWhereToWatch}
                  >
                    <Info className="w-5 h-5" />
                    <span>Where to Watch</span>
                  </button>
                )}

                {series.trailer && (
                  <button
                    className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                    onClick={() => setTrailerPlaying(true)}
                  >
                    <Play className="w-5 h-5" />
                    <span>Trailer</span>
                  </button>
                )}

                {isAuthenticated && (
                  <>
                    <button
                      className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                      onClick={toggleBookmark}
                    >
                      {isBookmarked ? (
                        <>
                          <i className="fas fa-bookmark text-yellow-500 text-lg" />
                          <span className="text-yellow-500">In Favorites</span>
                        </>
                      ) : (
                        <>
                          <i className="far fa-bookmark text-white text-lg" />
                          <span>Favorites</span>
                        </>
                      )}
                    </button>

                    <button
                      className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                      onClick={handleWhereToWatch}
                    >
                      <Info className="w-5 h-5" />
                      <span>View More</span>
                    </button>
                  </>
                )}

                <button
                  className="flex items-center gap-2 px-8 py-3 bg-gray-800 text-white rounded-full font-semibold hover:bg-gray-700 transition-colors"
                  onClick={handleShare}
                >
                  <Share2 className="w-5 h-5" />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>

          {/* Down Arrow Animation - Bottom Right */}
          <div className="absolute bottom-8 right-8 z-30">
            <Lottie
              animationData={downArrowAnimation}
              autoplay={true}
              loop={true}
              style={{ width: 60, height: 60 }}
            />
          </div>
        </div>

        {/* Cast Section */}
        {series.cast && series.cast.length > 0 && (
          <div className="relative px-8 lg:px-16 py-12 z-10 overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl font-semibold mb-6">Cast & Characters</h2>
            </div>
            <div className="max-w-7xl mx-auto">
              <div
                className="overflow-hidden select-none -mr-8 lg:-mr-16"
                ref={castEmblaRef}
              >
                <div
                  className="flex gap-4"
                  style={{ padding: "10px 10px 10px 10px" }}
                >
                  {series.cast.map((actor, index) => {
                    const castId =
                      actor.id ||
                      actor.castId ||
                      actor.tmdbId ||
                      actor.personId ||
                      actor.person_id ||
                      actor.credit_id ||
                      actor.cast_id;

                    return (
                      <div
                        className="flex-shrink-0 w-32 text-center cursor-pointer group"
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (castId) {
                            navigate(`/web-player/cast/${castId}`);
                          }
                        }}
                        onKeyDown={activateOnEnterOrSpace}
                      >
                        <div className="w-32 h-32 rounded-full overflow-hidden mb-2 mx-auto border-4 border-transparent group-hover:border-white group-hover:scale-110 transition-all duration-200">
                          {actor.profilePath ? (
                            <img
                              alt={actor.name}
                              className="w-full h-full object-cover"
                              draggable="false"
                              src={actor.profilePath}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-700">
                              <svg
                                className="w-12 h-12 text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  clipRule="evenodd"
                                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  fillRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <p className="text-sm font-medium truncate text-white">
                          {actor.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {actor.character}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Episodes and Suggested Tabs Section */}
        {((series.episodes && series.episodes.length > 0) ||
          (series.recommendations && series.recommendations.length > 0)) && (
          <div className="relative px-8 lg:px-12 py-12 z-10">
            <div className="max-w-7xl mx-auto">
              {/* Tabs */}
              <div className="flex gap-8 mb-8 border-b border-gray-700">
                {series.episodes && series.episodes.length > 0 && (
                  <button
                    className={`pb-4 px-2 text-lg font-semibold transition-colors ${
                      activeTab === "episodes"
                        ? "text-white border-b-2 border-white"
                        : "text-gray-400 hover:text-white"
                    }`}
                    onClick={() => setActiveTab("episodes")}
                  >
                    Episodes
                  </button>
                )}
                {series.recommendations &&
                  series.recommendations.length > 0 && (
                    <button
                      className={`pb-4 px-2 text-lg font-semibold transition-colors ${
                        activeTab === "suggested"
                          ? "text-white border-b-2 border-white"
                          : "text-gray-400 hover:text-white"
                      }`}
                      onClick={() => setActiveTab("suggested")}
                    >
                      More like this
                    </button>
                  )}
              </div>

              {activeTab === "episodes" && (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                  {/* Season Selector - Left Side */}
                  <div className="lg:col-span-1">
                    <div className="sticky top-4">
                      {series.seasons &&
                        Array.isArray(series.seasons) &&
                        series.seasons
                          .filter((s) => s.season !== 0)
                          .map((season) => {
                            // Count episodes for this season
                            const episodeCount =
                              season.episodes?.length ||
                              series.episodes?.filter(
                                (ep) => ep.season === season.season
                              ).length ||
                              0;

                            return (
                              <button
                                className={`block w-full text-left p-4 mb-2 rounded-lg transition-colors ${
                                  selectedSeason === season.season
                                    ? "bg-gray-800 text-white"
                                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                                }`}
                                key={season.season}
                                onClick={() => setSelectedSeason(season.season)}
                              >
                                <div className="font-medium">
                                  Season {season.season}
                                </div>
                                {episodeCount > 0 && (
                                  <div
                                    className={`text-sm mt-0.5 ${
                                      selectedSeason === season.season
                                        ? "text-gray-400"
                                        : "text-gray-500"
                                    }`}
                                  >
                                    {episodeCount} Episode
                                    {episodeCount !== 1 ? "s" : ""}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                    </div>
                  </div>

                  {/* Episodes List - Right Side */}
                  <div className="lg:col-span-3 space-y-4">
                    {seasonEpisodes?.map((episode) => (
                      <div
                        className="flex gap-4 p-4 bg-gray-900 rounded-lg hover:bg-gray-800 cursor-pointer transition-colors"
                        key={episode.episode}
                        role="button"
                        tabIndex={0}
                        onClick={() => handlePlayEpisode(episode)}
                        onKeyDown={activateOnEnterOrSpace}
                      >
                        {/* Episode Thumbnail */}
                        <div className="flex-shrink-0 w-48 h-28 bg-gray-700 rounded-lg overflow-hidden relative">
                          {episode.posterPath ? (
                            <img
                              alt={episode.name}
                              className="w-full h-full object-cover"
                              src={episode.posterPath}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="w-8 h-8 text-gray-500" />
                            </div>
                          )}

                          {/* Progress Bar - Show if episode has progress */}
                          {(() => {
                            const episodeMediaId = `${series.mediaId || series.id}-${episode.episodeId || episode.id}`;
                            const progress = episodeProgress[episodeMediaId];

                            if (progress > 0) {
                              return (
                                <div
                                  className="absolute left-0 right-0 h-1 bg-gray-600/50 z-10"
                                  style={{ bottom: "5px" }}
                                >
                                  <div
                                    className="h-full transition-[width] duration-300"
                                    style={{
                                      backgroundColor: "#6495ED",
                                      width: `${Math.min(Math.max(0, progress), 100)}%`,
                                    }}
                                  />
                                </div>
                              );
                            }

                            return null;
                          })()}
                        </div>

                        {/* Episode Info */}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold mb-2">
                            {formatEpisodeTitle(episode)}
                          </h3>
                          {episode.description && (
                            <p className="text-gray-400 text-sm mb-2 line-clamp-2">
                              {episode.description}
                            </p>
                          )}
                          <div className="text-sm text-gray-500">
                            {formatDuration(
                              episode.duration ||
                                episode.runtime ||
                                episode.runTime ||
                                episode.length ||
                                episode.episode_run_time
                            )}
                          </div>
                        </div>

                        {/* Play Button */}
                        <div className="flex items-center">
                          <button className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors">
                            <Play className="w-6 h-6" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "suggested" && series.recommendations && (
                <div>
                  <SeriesCarousel
                    items={series.recommendations}
                    itemsVisible={6.8}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <style>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        {/* Video Player Modal */}
        {playerOpen && (
          <PlyrVideoPlayer
            backdropPath={series?.backdropPath}
            episodeId={selectedEpisode?.episodeId || selectedEpisode?.id}
            episodeInfo={selectedEpisode || null}
            isOpen={playerOpen}
            mediaId={
              selectedEpisode
                ? `${series?.mediaId || series?.id}-${selectedEpisode.episodeId || selectedEpisode.id}`
                : series?.mediaId || series?.id
            }
            mediaType="tv"
            posterPath={series?.posterPath}
            seriesId={series?.mediaId || series?.id}
            streamUrl={currentStreamUrl}
            title={currentStreamTitle}
            onClose={() => {
              setPlayerOpen(false);
              setSelectedEpisode(null);
              setCurrentStreamUrl(null);
              setCurrentStreamTitle(null);
              // Refresh episode progress after closing player
              fetchEpisodeProgress();
            }}
          />
        )}

        {/* Trailer Modal */}
        {trailerPlaying && series.trailer && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setTrailerPlaying(false)}
          >
            <div
              className="relative w-full max-w-5xl aspect-video"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute -top-12 right-0 text-white hover:text-gray-300"
                onClick={() => setTrailerPlaying(false)}
              >
                <X className="w-8 h-8" />
              </button>
              <iframe
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                className="w-full h-full rounded-lg"
                frameBorder="0"
                src={`https://www.youtube.com/embed/${extractYouTubeId(series.trailer)}?autoplay=1`}
                title="Trailer"
              />
            </div>
          </div>
        )}

        {/* Description Overlay */}
        {showDescriptionOverlay && (series.description || series.overview) && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setShowDescriptionOverlay(false)}
          >
            <div
              className="relative w-full max-w-2xl bg-gray-900 rounded-2xl p-6 max-h-[80vh] overflow-y-auto"
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-4 right-4 text-white hover:text-gray-300"
                onClick={() => setShowDescriptionOverlay(false)}
              >
                <X className="w-6 h-6" />
              </button>
              <h3 className="text-xl font-bold text-white mb-4">
                {series.name || series.title}
              </h3>
              <p className="text-gray-300 leading-relaxed">
                {series.description || series.overview}
              </p>
            </div>
          </div>
        )}

        {/* Where to Watch Modal */}
        {showProviders && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            role="presentation"
            onClick={() => setShowProviders(false)}
          >
            <div
              className="relative w-full max-w-4xl bg-gray-900 rounded-lg overflow-hidden"
              role="presentation"
              style={{
                backgroundImage: 'url("/empty_seats.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/80" />

              {/* Content */}
              <div className="relative z-10 p-8">
                <button
                  className="absolute top-4 right-4 text-white hover:text-gray-300"
                  onClick={() => setShowProviders(false)}
                >
                  <X className="w-8 h-8" />
                </button>

                <h2 className="text-3xl font-bold text-white mb-2 text-center">
                  Where to watch
                </h2>
                <p className="text-xl text-gray-300 mb-8 text-center">
                  {series.name}
                </p>

                {/* Loading State */}
                {loadingProviders ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500" />
                  </div>
                ) : (
                  <>
                    {/* Streaming Providers Grid */}
                    <div className="flex flex-wrap justify-center gap-6 mb-8">
                      {streamingProviders && streamingProviders.length > 0 ? (
                        streamingProviders.map((provider, index) => (
                          <a
                            className="group relative transition-all hover:scale-110 cursor-pointer"
                            href={
                              provider.deepLink ||
                              provider.link ||
                              provider.url ||
                              "#"
                            }
                            key={index}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            <div className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center">
                              {provider.logoPath ||
                              provider.logo ||
                              provider.icon ? (
                                <img
                                  alt={
                                    provider.provider ||
                                    provider.name ||
                                    provider.providerName
                                  }
                                  className="w-full h-full object-cover"
                                  src={
                                    provider.logoPath ||
                                    provider.logo ||
                                    provider.icon
                                  }
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-700 rounded-full">
                                  <span className="text-white font-semibold text-sm text-center px-2">
                                    {provider.provider ||
                                      provider.name ||
                                      provider.providerName}
                                  </span>
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-center text-gray-300 mt-2 max-w-[96px] mx-auto truncate">
                              {provider.provider ||
                                provider.name ||
                                provider.providerName}
                            </p>
                          </a>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-400 text-lg">
                            No streaming providers available
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Common Providers if no specific ones */}
                    {(!streamingProviders ||
                      streamingProviders.length === 0) && (
                      <div className="flex flex-wrap justify-center gap-6 mb-8">
                        {[
                          "AMC+",
                          "Fandango",
                          "Netflix",
                          "Prime Video",
                          "Hulu",
                          "Disney+",
                          "Apple TV+",
                          "Peacock",
                        ].map((provider) => (
                          <div
                            className="group relative transition-all hover:scale-110 cursor-pointer"
                            key={provider}
                          >
                            <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center">
                              <span className="text-white font-semibold text-sm text-center px-2">
                                {provider}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tip */}
                    <div className="text-center text-gray-400 text-sm">
                      <p>Tip: Tap any provider's logo to open its website</p>
                      <p>
                        Check each service for current subscription deals and
                        trial offers
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Premium Overlay */}
        <PremiumOverlay
          feature="series"
          isOpen={showPremiumOverlay}
          onClose={() => setShowPremiumOverlay(false)}
        />
      </div>
    </>
  );
};

export default WebPlayerSeriesDetails;
